import React, { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';

import { Icon } from '@chakra-ui/react';
import { PERMISSIONS } from 'constants/permissions';
import { AdminTabRedirect } from 'utils/adminTabRedirect';
import {
  MdHome,
  MdLock,
  MdPeople,
  MdEventNote,
  MdPayments,
  MdBuild,
  MdSettings,
  MdFingerprint,
  MdMail,
  MdBookmark,
  MdLocalPharmacy,
  MdAttachMoney,
  MdAssessment,
  MdDescription,
  MdHealing,
} from 'react-icons/md';

// Admin Imports (lazy to avoid circular dependency)
const MainDashboard = lazy(() => import('views/admin/default'));
const Clients = lazy(() => import('views/admin/clients'));
const MedicalRecordDetail = lazy(() => import('views/admin/medical-records/detail'));
const ReceptionHub = lazy(() => import('views/admin/reception'));
const Schedule = lazy(() => import('views/admin/schedule'));
const FinanceHub = lazy(() => import('views/admin/finance'));
const Masters = lazy(() => import('views/admin/masters'));
const Attendance = lazy(() => import('views/admin/attendance'));
const SettingsHub = lazy(() => import('views/admin/settings'));
const FeedbackInbox = lazy(() => import('views/admin/feedback-inbox'));
const MedicalTemplates = lazy(() => import('views/admin/medical-templates'));
const PharmacyHub = lazy(() => import('views/admin/pharmacy'));
const StaffPaymentsPage = lazy(() => import('views/admin/staff-payments'));
const ReportsPage = lazy(() => import('views/admin/reports'));
const MonthlyFinanceReportPage = lazy(() => import('views/admin/reports/MonthlyFinanceReport'));
const DoctorWorkloadReportPage = lazy(() => import('views/admin/reports/DoctorWorkloadReport'));
const ProtocolsArchivePage = lazy(() => import('views/admin/protocols'));
const SurgeryReceptionPage = lazy(() => import('views/admin/surgery'));

// Auth Imports
const SignInCentered = lazy(() => import('views/auth/signIn'));

const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>Loading...</div>
);

const routes = [
  {
    name: 'Панель управления',
    layout: '/admin',
    path: '/dashboard',
    permission: PERMISSIONS.DASHBOARD,
    icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <MainDashboard />
      </Suspense>
    ),
  },
  {
    name: 'Приём',
    layout: '/admin',
    path: '/reception',
    permissions: [
      PERMISSIONS.UPCOMING_APPOINTMENTS,
      PERMISSIONS.SCHEDULE,
      PERMISSIONS.MEDICAL_RECORDS,
      PERMISSIONS.IMAGING_STUDIES,
      PERMISSIONS.CLIENTS,
    ],
    icon: <Icon as={MdEventNote} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <ReceptionHub />
      </Suspense>
    ),
  },
  {
    name: 'Хирургия',
    layout: '/admin',
    path: '/surgery',
    permissions: [PERMISSIONS.SCHEDULE, PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.CLIENTS],
    icon: <Icon as={MdHealing} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <SurgeryReceptionPage />
      </Suspense>
    ),
  },
  {
    name: 'Пациенты',
    layout: '/admin',
    path: '/clients',
    permissions: [
      PERMISSIONS.CLIENTS,
      PERMISSIONS.MEDICAL_RECORDS,
      PERMISSIONS.IMAGING_STUDIES,
      PERMISSIONS.RECEPTION_LEADS,
    ],
    icon: <Icon as={MdPeople} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <Clients />
      </Suspense>
    ),
  },
  {
    name: 'Протоколы',
    layout: '/admin',
    path: '/protocols',
    adminOrDirector: true,
    icon: <Icon as={MdDescription} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtocolsArchivePage />
      </Suspense>
    ),
  },
  {
    name: 'Финансы',
    layout: '/admin',
    path: '/finance',
    permissions: [PERMISSIONS.EXPENSES, PERMISSIONS.PAYROLL, PERMISSIONS.DASHBOARD_FINANCE],
    icon: <Icon as={MdPayments} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <FinanceHub />
      </Suspense>
    ),
  },
  {
    name: 'Отчёты',
    layout: '/admin',
    path: '/reports',
    directorOnly: true,
    permission: PERMISSIONS.DASHBOARD_FINANCE,
    icon: <Icon as={MdAssessment} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <ReportsPage />
      </Suspense>
    ),
  },
  {
    name: 'Отчёт по месяцам',
    layout: '/admin',
    path: '/reports/monthly',
    directorOnly: true,
    permission: PERMISSIONS.DASHBOARD_FINANCE,
    hideInSidebar: true,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <MonthlyFinanceReportPage />
      </Suspense>
    ),
  },
  {
    name: 'Аналитика врачей',
    layout: '/admin',
    path: '/reports/doctors',
    directorOnly: true,
    permission: PERMISSIONS.DASHBOARD_FINANCE,
    hideInSidebar: true,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <DoctorWorkloadReportPage />
      </Suspense>
    ),
  },
  {
    name: 'Платежи',
    layout: '/admin',
    path: '/payments',
    permission: PERMISSIONS.DASHBOARD_FINANCE,
    icon: <Icon as={MdAttachMoney} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <StaffPaymentsPage />
      </Suspense>
    ),
  },
  {
    name: 'Справочник',
    layout: '/admin',
    path: '/masters',
    directorOnly: true,
    permissions: [PERMISSIONS.MASTERS, PERMISSIONS.SERVICES],
    icon: <Icon as={MdBuild} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <Masters />
      </Suspense>
    ),
  },
  {
    name: 'Табель посещений',
    layout: '/admin',
    path: '/attendance',
    permission: PERMISSIONS.ATTENDANCE,
    icon: <Icon as={MdFingerprint} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <Attendance />
      </Suspense>
    ),
  },
  {
    name: 'Шаблоны',
    layout: '/admin',
    path: '/templates',
    permissions: [PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.IMAGING_STUDIES],
    icon: <Icon as={MdBookmark} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <MedicalTemplates />
      </Suspense>
    ),
  },
  {
    name: 'Обратная связь',
    layout: '/admin',
    path: '/feedback-inbox',
    permission: PERMISSIONS.FEEDBACK_INBOX,
    icon: <Icon as={MdMail} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <FeedbackInbox />
      </Suspense>
    ),
  },
  {
    name: 'Аптека',
    layout: '/admin',
    path: '/pharmacy',
    permission: PERMISSIONS.PHARMACY,
    icon: <Icon as={MdLocalPharmacy} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <PharmacyHub />
      </Suspense>
    ),
  },
  {
    name: 'Настройки',
    layout: '/admin',
    path: '/settings',
    permissions: [PERMISSIONS.SETTINGS_AI, PERMISSIONS.SETTINGS_USERS, PERMISSIONS.MAILINGS],
    icon: <Icon as={MdSettings} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <SettingsHub />
      </Suspense>
    ),
  },
  {
    name: 'Предстоящие записи',
    layout: '/admin',
    path: '/upcoming',
    hideInSidebar: true,
    permission: PERMISSIONS.UPCOMING_APPOINTMENTS,
    component: <AdminTabRedirect basePath="/reception" tab="upcoming" />,
  },
  {
    name: 'Расписание',
    layout: '/admin',
    path: '/schedule',
    hideInSidebar: true,
    permission: PERMISSIONS.SCHEDULE,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <Schedule standalone />
      </Suspense>
    ),
  },
  {
    name: 'Стационар',
    layout: '/admin',
    path: '/inpatient',
    hideInSidebar: true,
    permissions: [PERMISSIONS.SCHEDULE, PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.CLIENTS],
    component: <AdminTabRedirect basePath="/reception" tab="inpatient" />,
  },
  {
    name: 'Воронка лидов',
    layout: '/admin',
    path: '/reception-leads',
    hideInSidebar: true,
    permission: PERMISSIONS.RECEPTION_LEADS,
    component: <AdminTabRedirect basePath="/clients" tab="leads" />,
  },
  {
    name: 'Медкнижки',
    layout: '/admin',
    path: '/medical-records',
    hideInSidebar: true,
    permissions: [PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.IMAGING_STUDIES],
    component: <Navigate to="/admin/clients" replace />,
  },
  {
    name: 'Медкнижка пациента',
    layout: '/admin',
    path: '/medical-records/:patientId',
    permissions: [PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.IMAGING_STUDIES],
    hideInSidebar: true,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <MedicalRecordDetail />
      </Suspense>
    ),
  },
  {
    name: 'Рассылки',
    layout: '/admin',
    path: '/mailings',
    hideInSidebar: true,
    permission: PERMISSIONS.MAILINGS,
    component: <Navigate to="/admin/settings?tab=mailings" replace />,
  },
  {
    name: 'Услуги',
    layout: '/admin',
    path: '/services',
    hideInSidebar: true,
    directorOnly: true,
    permission: PERMISSIONS.SERVICES,
    component: <Navigate to="/admin/masters?tab=services" replace />,
  },
  {
    name: 'Sign In',
    layout: '/auth',
    path: '/sign-in',
    icon: <Icon as={MdLock} width="20px" height="20px" color="inherit" />,
    component: (
      <Suspense fallback={<LoadingFallback />}>
        <SignInCentered />
      </Suspense>
    ),
  },
];

export default routes;
